import React, { useEffect, useRef, useState } from 'react';
import './App.scss';
import { gsap, ScrollTrigger } from './gsap';
import Intro from './Components/Intro/Intro';
import Header from './Components/common-header/Header';
import NavBar from './Components/NavBar/NavBar';
import MobileMenu from './Components/MobileMenu/MobileMenu';
import Eras from './Components/Eras/Eras';

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const appRef = useRef(); //for detecting scroll direction for nav bar
  const navBarRef = useRef(); //for fixed position when scrolled up
  const headerRef = useRef(); //for positioning mobile menubar
  const topBarRef = useRef(); //top bar of hamburger icon at nav bar for animation
  const bottomBarRef = useRef(); //bottom bar of hamburger icon at nav bar for animation
  const circleRef = useRef(); // circle icon for close button at mobile menu for animation
  const mobileMenuRef = useRef(); //for showing/hiding mobile menu
  const [screenWidth, setScreenWidth] = useState(window.innerWidth); //for resize observer
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [currentEra, setCurrentEra] = useState(3);
  const [showArrow, setShowArrow] = useState(true);

  //show/hide mobile menu
  const positionMobileMenu = () => {
    menuOpen ? setMenuOpen(false) : setMenuOpen(true);
  };

  useEffect(() => {
    const tl = gsap.timeline();
    menuOpen
      ? tl
          .to(mobileMenuRef.current, { x: 0, duration: 0.3 })
          .to(topBarRef.current, {
            y: 3.5,
            rotation: 135,
            duration: 0.5,
            scale: 0.5
          })
          .to(bottomBarRef.current, { y: -3.5, duration: 0.5, rotation: 45, scale: 0.5 }, '-=0.5')
          .to(circleRef.current, { scale: 1, opacity: 1, duration: 0.5 })
      : tl
          .to(circleRef.current, { scale: 0.5, opacity: 0, duration: 0.2 })
          .to(topBarRef.current, {
            y: 0,
            rotation: 0,
            duration: 0.5,
            scale: 1
          })
          .to(bottomBarRef.current, { y: 0, duration: 0.5, rotation: 0, scale: 1 }, '-=0.5')
          .to(mobileMenuRef.current, { x: '100%', duration: 0.3 });
  }, [menuOpen]);

  //keeps mobile menu below the common header
  useEffect(() => {
    const header = headerRef.current;
    const menuStyle = mobileMenuRef.current.style;
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach((entry) => {
          if (entry['isIntersecting'] === false) {
            menuStyle.position = 'fixed';
            menuStyle.top = 0;
          } else {
            menuStyle.position = 'absolute';
            menuStyle.top = '64px';
          }
        });
      },
      { threshold: [0] }
    );
    observer.observe(header);
  }, []);

  //hides nav bar on scroll down & shows on scroll up
  useEffect(() => {
    ScrollTrigger.create({
      trigger: appRef.current,
      start: 'top -20%',
      pinSpacer: false,
      onUpdate: (self) => {
        if (self.direction === 1) {
          gsap.to(navBarRef.current, { y: '-100%', duration: 1, ease: 'ease-out' });
        } else if (self.direction === -1) {
          gsap.to(navBarRef.current, { y: '0', duration: 1, ease: 'ease-in' });
        }
      }
    });
  }, []);

  useEffect(() => {
    ScrollTrigger.create({
      trigger: navBarRef.current,
      endTrigger: appRef.current,
      end: 'bottom bottom',
      start: 'top top',
      pin: true,
      scrub: 1,
      pinSpacer: false
    });
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      const height = entries[0].contentRect.height;
      if (width !== screenWidth) {
        setScreenWidth(width);
      }
      if (height !== screenHeight) {
        setScreenHeight(height);
      }
    });
    resizeObserver.observe(appRef.current);
  }, [screenWidth, screenHeight]);

  return (
    <div
      className="app"
      ref={appRef}
      onTouchStart={() => {
        setShowArrow(false);
      }}
    >
      <div className="app__background" />
      <Header headerRef={headerRef} />
      <div className="app__menuContainer">
        <MobileMenu
          mobileMenuRef={mobileMenuRef}
          circleRef={circleRef}
          topBarRef={topBarRef}
          bottomBarRef={bottomBarRef}
          positionMobileMenu={positionMobileMenu}
        />
      </div>
      <NavBar positionMobileMenu={positionMobileMenu} navBarRef={navBarRef} />
      <Intro
        navBarRef={navBarRef}
        currentEra={currentEra}
        setCurrentEra={setCurrentEra}
        screenWidth={screenWidth}
        showArrow={showArrow}
      />

      <Eras screenWidth={screenWidth} screenHeight={screenHeight} currentEra={currentEra} />
    </div>
  );
}

export default App;
